import { Button, Group } from '@mantine/core';
import { AiOutlinePlus, AiOutlineDelete, AiOutlineReload } from 'react-icons/ai';
import { useState } from 'react';
import { Customer } from '../types';
import { 
    createStripeCustomer, 
    updateStripeCustomer, 
    deleteStripeCustomer 
} from '../../stripe-api-integration/services/StripeCustomerService';

interface StripeCustomerButtonsProps {
    customer: Customer;
    onCustomerUpdated?: (stripeCustomerId: string) => void;
}

export const StripeCustomerButtons = ({ customer, onCustomerUpdated }: StripeCustomerButtonsProps) => {
    const [loading, setLoading] = useState(false);
    
    const hasStripeCustomer = customer.billingInfo?.billingProviderRecordId;
    const isStripeProvider = !!customer.billingInfo?.billingProviderId; // Show for any billing provider

    const handleCreateStripeCustomer = async () => {
        if (!isStripeProvider) return;
        
        setLoading(true);
        try {
            const stripeCustomer = await createStripeCustomer(customer.id, {
                name: customer.name,
                description: customer.description,
                email: customer.contactInfo?.email,
                phone: customer.contactInfo?.phone
            });
            
            if (stripeCustomer?.id && onCustomerUpdated) {
                onCustomerUpdated(stripeCustomer.id);
            }
        } catch (error) {
            console.error('Failed to create Stripe customer:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStripeCustomer = async () => {
        if (!isStripeProvider || !hasStripeCustomer) return;
        
        setLoading(true);
        try {
            await updateStripeCustomer(customer.billingInfo!.billingProviderRecordId!, customer.id, {
                name: customer.name,
                description: customer.description,
                email: customer.contactInfo?.email,
                phone: customer.contactInfo?.phone
            });
        } catch (error) {
            console.error('Failed to update Stripe customer:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteStripeCustomer = async () => {
        if (!isStripeProvider || !hasStripeCustomer) return;
        
        setLoading(true);
        try {
            await deleteStripeCustomer(customer.billingInfo!.billingProviderRecordId!, customer.id);
            if (onCustomerUpdated) {
                onCustomerUpdated('');
            }
        } catch (error) {
            console.error('Failed to delete Stripe customer:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isStripeProvider) {
        return null;
    }

    return (
        <Group gap="xs">
            {!hasStripeCustomer ? (
                <Button
                    leftSection={<AiOutlinePlus size={16} />}
                    size="xs"
                    variant="light"
                    loading={loading}
                    onClick={handleCreateStripeCustomer}
                >
                    Create in Stripe
                </Button>
            ) : (
                <>
                    <Button
                        leftSection={<AiOutlineReload size={16} />}
                        size="xs"
                        variant="light"
                        loading={loading}
                        onClick={handleUpdateStripeCustomer}
                    >
                        Update Stripe
                    </Button>
                    <Button
                        leftSection={<AiOutlineDelete size={16} />}
                        size="xs"
                        variant="light"
                        color="red"
                        loading={loading}
                        onClick={handleDeleteStripeCustomer}
                    >
                        Delete from Stripe
                    </Button>
                </>
            )}
        </Group>
    );
};